import { Component, computed, OnInit, signal, ViewChild, ChangeDetectionStrategy, isDevMode } from "@angular/core";
import { Router } from "@angular/router";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Observable, fromEvent, merge } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { Auth } from "aws-amplify";
import { CorpClientsService } from "../corp-clients.service";
import { Client, CorpClient } from "../shared-models/client.model";
import { environment } from "src/environments/environment";
import { MatSidenav } from "@angular/material/sidenav";
import { AppAnalyticsService } from "src/app-analytics.service";
import { AppStorageService, LS_RECORDER_TYPE } from "../app-storage.service";
import { AuthMonitorService } from "../auth/auth-monitor.service";
import { UsersService } from "../users/users.service";
import { AppUser } from "../users/users";
import { SnackbarService } from "../snackbar.service";
import { SHERPA_CRM } from "../static/crm.static";
import { CrmService } from "../crm/crm.service";
import { DarkModeService } from "../dark-mode.service";
import { SsoService } from "../auth/sso.service";
import { DevAWSSettings, ProdAWSSettings } from "src/awsconfig";

export interface AppNavItem {
  path: string;
  icon: string;
  name: string;
}
@Component({
    selector: "app-nav",
    templateUrl: "./nav.component.html",
    styleUrls: ["./nav.component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavComponent {
  username = "";
  allClients: CorpClient[];
  currentClient: CorpClient;
  appVersion = "";
  readonly RecordNavTitle = "Record"; // Changed again - SM-1540
  readonly crmItem: AppNavItem = { path: "/crm", icon: "id-card", name: "CRM" }; // Conditionally added
  readonly scriptLibraryItem: AppNavItem = { path: "/script-library", icon: "scroll", name: "Script Library" }; // Conditionally added
  readonly videoLibraryItem: AppNavItem = { path: "/video-library", icon: "photo-film", name: "Corporate Videos" }; // Conditionally added
  readonly corporateCtasItem: AppNavItem = { path: "/ctas/corporate", icon: "bullhorn", name: "Shared CTAs" }; // Conditionally added

  adminNavItems = [
    { path: "/", icon: "chart-bar", name: "Dashboard" },
    { path: "/feed", icon: "stream", name: "Activity" },
    { path: "/results", icon: "chart-area", name: "Team Videos" },
    { path: "/my-videos", icon: "chart-line", name: "My Videos" },
    { path: "/users", icon: "users", name: "Users" },
    { path: "/reports", icon: "file-alt", name: "Reports" },
    { path: "/snap", icon: "video", name: this.RecordNavTitle },
    { path: "/ctas", icon: "bullhorn", name: "CTA Library" },
    { path: "/qrpro", icon: "qrcode", name: "QR Pro" },    
  ];
  userNavItems = [
    { path: "/", icon: "chart-bar", name: "Dashboard" },
    { path: "/feed", icon: "stream", name: "Activity" },
    { path: "/my-videos", icon: "chart-line", name: "My Videos" },
    { path: "/reports", icon: "file-alt", name: "Reports" },
    { path: "/snap", icon: "video", name: this.RecordNavTitle },
    { path: "/ctas", icon: "bullhorn", name: "CTA Library" },
    { path: "/qrpro", icon: "qrcode", name: "QR Pro" },    
  ];
  navItems: AppNavItem[] = [];
  is_admin: boolean = false;
  appUser: AppUser;
  appUserClient: Client;

  private setting = isDevMode() ? new DevAWSSettings() : new ProdAWSSettings();
  readonly sidekickFallbackUrl = "https://www.salesmail.com/sidekick"; // Same across all environments

  isLoading = signal(true);

  /**
   * False when the account does not include the SalesMail product. The nav is
   * still rendered in full, but every SalesMail destination is made inert --
   * see the template. Sidekick, Help Center, the dark mode toggle and Sign Out
   * stay live.
   *
   * Read from the service rather than snapshotted here, so it always agrees
   * with what SalesMailAccessGuard decided on the current navigation. A local
   * copy set once in getUser() could not: a failed bootstrap left the nav dead
   * for the rest of the session even after a later navigation succeeded, and a
   * cache cleared mid-session left it fully enabled while the guard bounced
   * every click.
   */
  hasSalesMailAccess = computed(() => this.userService.salesMailAccess());

  /**
   * Whether SalesMail destinations should be inert right now.
   *
   * The guard resolves entitlement before this component is constructed, so the
   * access term is normally settled already. isLoading() covers the rest of the
   * bootstrap: the sidebar list hides behind the loading skeleton, but the
   * toolbar renders immediately, and its controls should not be clickable
   * before the account has finished loading.
   */
  smActionsDisabled = computed(() => this.isLoading() || !this.hasSalesMailAccess());

  isDark = computed(() => {
    return this.darkmodeService.isDark();
  });

  @ViewChild('drawer') drawer: MatSidenav ; // side naviation drawer
  isHandset$: Observable<boolean> = merge(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((result) => result.matches)),
    fromEvent(window, 'resize').pipe(
      startWith(null),
      map(() => window.innerWidth < 1000)
    )
  ).pipe(
    map((isHandset) => isHandset || window.innerWidth < 1000)
  );

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    public corpService: CorpClientsService,
    private analyticService: AppAnalyticsService,
    private appStorage: AppStorageService,
    private authMonitor: AuthMonitorService,
    private userService: UsersService,
    private snackbarService: SnackbarService,
    private crmService: CrmService,
    private darkmodeService: DarkModeService,
    private ssoService: SsoService
  ) {
    this.corpService.currentClient.subscribe((result) => {
      this.currentClient = result;
    });

    this.corpService.allClients.subscribe((result) => {
      this.allClients = result;
    });

    this.appVersion = environment.version;
  }

  ngOnInit() {
    this.getUser();    
  }

  async getUser() {
    try {
      this.isLoading.set(true);
      this.username = await this.userService.getAuthenticatedUserEmail();
      const isClientAdmin = await this.userService.isAuthenticatedUserClientAdmin();
      if (isClientAdmin) {
        this.navItems = this.adminNavItems;
        this.is_admin = true;
      } else {
        this.navItems = this.userNavItems;
      }
      this.appUser = await this.userService.getCurrentSalesMailUser(); // Gets DB value of user 
      this.appUserClient = await this.userService.getCurrentSalesMailUserClient();

      // Populates the shared signal the template reads. Normally already done
      // by the guard for this navigation; cheap either way, the user is cached.
      await this.userService.hasSalesMailAccess();
      
      /**
       * Show script library if:
       *  * The client has it enabled and the user is an admin
       *  * or the client has it enable and the client has enabled it for all users
       */
      if (this.appUserClient.enable_script_library) {
        if (isClientAdmin || this.appUserClient.enable_script_library_users) {
          this.navItems = [...this.navItems, this.scriptLibraryItem];       
        }
      }

      /** Parent and child account users have access to video library option */
      if (this.appUserClient.account_type == 'parent' || this.appUserClient.account_type == 'child') {
        this.navItems = [...this.navItems, this.videoLibraryItem];
      }

      /** Admin/Corporate users on parent or child accounts manage shared Shared CTAs */
      if ((this.appUserClient.account_type == 'parent' || this.appUserClient.account_type == 'child') && isClientAdmin) {
        this.navItems = [...this.navItems, this.corporateCtasItem];
      }

      /**
       * If user has CRM 3 (Sherpa) then show the CRM nav item, others likely to follow
       * For now only show CRM for Sherpa
       */
      if (this.appUser?.crm_id === SHERPA_CRM) {
        this.navItems = [...this.navItems, this.crmItem];        
      }

      /**
       * If the user has any crm id, use the crm service to init it. Service handles each type.
       * Skipped without SalesMail access: nothing that consumes the CRM is reachable.
       */
      if (this.appUser.crm_id && this.hasSalesMailAccess()) {
        this.crmService.initCRM();
      }

    } catch (error) {
      console.error(error);
      this.snackbarService.showErrorMessageSnackBar("There was a problem loading your account");
    } finally {
      this.isLoading.set(false);
    } 
  }

  async signOut() {
    this.analyticService.log('nav_sign_out');

    // SSO (federated) users require a global sign-out so the Microsoft/Cognito
    // hosted session is invalidated. ssoSignOut() clears local state and redirects
    // to the Cognito Hosted UI /logout endpoint.
    const isSso = await this.ssoService.isCurrentUserSso();

    this.userService.clearCurrentUser();
    this.userService.clearCurrentUserClient();
    this.corpService.reset();
    this.authMonitor.stopMonitor(); // Is auto restarted when user sucessfully auths

    if (isSso) {
      await this.ssoService.ssoSignOut();
      return; // ssoSignOut() redirects the browser to Cognito /logout
    }

    Auth.signOut()
      .then((data) => {
        this.router.navigate(["/signin"]);
      })
      .catch((err) => console.log(err));
  }

  goToHelpCenter() {
    const help_url = 'https://support.salesmail.com/portal/en/kb/heartlegacy';
    this.analyticService.log('nav_help_center', help_url)
    window.open(help_url, "_blank");
  }

  goToSidekick() {
    const url = this.appUser?.allow_sidekick ? this.setting.value.sidekickUrl : this.sidekickFallbackUrl;
    this.analyticService.log('nav_sidekick', url);
    window.open(url, "_blank");
    this.toggle();
  }

  updateCurrentclient(client: CorpClient) {    
    this.corpService.setCurrentClient(client);
    this.analyticService.log('nav_change_corp_client', client);
  }

  toggle() {
    if(this.drawer.mode == 'over') {
      this.drawer.toggle();
    }
  }

  async navigate(item: AppNavItem) {
    // Belt and braces: the template already makes these items inert, but the
    // guard is the thing that actually enforces access on the route itself.
    if (!this.hasSalesMailAccess()) {
      return;
    }

    if (item.name === this.RecordNavTitle) {
      const recorderType = (await this.appStorage.getRecorder()) ?? 'snap';
      const path = recorderType === 'original' ? '/record' : '/snap';
      this.router.navigate([path]);
    } else {
      this.router.navigate([item.path]);
    }
  
    this.toggle();
  }

  toggleDarkMode() {
    this.darkmodeService.toggle();
  }

}
